import React, { Component } from "react";
import { Form } from "react-bootstrap";
import ConvoMediaFiles from "./convoFiles";
import WaTemplates from "./waTemplates";
import EmojiPicker from "emoji-picker-react";

class ConvoFooter extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isMobile: window.matchMedia("(max-width:600px)").matches,
            showEmoji: false,
            sendTemplate: false,
            popoverFilesTarget: false,
            selectedFiles: [],
        };
    }

    componentDidMount() {
        this.mediaQuery = window.matchMedia("(max-width:600px)");
        this.handleMQ = (e) => this.setState({ isMobile: e.matches });
        this.mediaQuery.addEventListener("change", this.handleMQ);
    }

    componentWillUnmount() {
        if (this.mediaQuery) this.mediaQuery.removeEventListener("change", this.handleMQ);
    }

    convertToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    }

    async handleFiles(files) {
        const filesWithBase64 = await Promise.all(
            files.map(async (file) => {
                const base64Data = await this.convertToBase64(file);
                return { file, base64Data, name: file.name, type: file.type, sizeMB: (file.size / (1024 * 1024)).toFixed(2) };
            })
        );
        this.setState({ selectedFiles: filesWithBase64, popoverFilesTarget: true });
    }

    // Optional write-permission gate. When the embedding screen passes `canSend`, every
    // write entry point (open template picker, attach photo/document, submit a message)
    // runs it first: it returns false and pops the parent's Access Denied modal when the
    // role lacks is_edit, and we stop BEFORE opening any modal — so Access Denied shows on
    // top instead of behind the template picker. Absent prop → never blocked (default use).
    isSendAllowed() {
        return typeof this.props.canSend !== "function" || this.props.canSend();
    }

    handleSpeedDialChange(actionName) {
        if (!this.isSendAllowed()) return;
        switch (actionName) {
            case "template":
                this.setState({ sendTemplate: true });
                break;
            case "photos": {
                const photoInput = document.createElement("input");
                photoInput.type = "file";
                photoInput.accept = "image/jpeg,image/jpg,image/png,video/mp4,audio/mp3";
                photoInput.multiple = true;
                photoInput.onchange = (e) => {
                    const files = Array.from(e.target.files).filter((file) =>
                        ["image/jpeg", "image/jpg", "image/png", "video/mp4", "audio/mp3"].includes(file.type)
                    );
                    this.handleFiles(files);
                };
                photoInput.click();
                break;
            }
            case "documents": {
                const docInput = document.createElement("input");
                docInput.type = "file";
                docInput.accept = ".pdf,.doc,.docx,.xls,.xlsx";
                docInput.multiple = true;
                docInput.onchange = (e) => {
                    const files = Array.from(e.target.files).filter((file) =>
                        [".pdf", ".doc", ".docx", ".xls", ".xlsx"].some((ext) =>
                            file.name.toLowerCase().endsWith(ext)
                        )
                    );
                    this.handleFiles(files);
                };
                docInput.click();
                break;
            }
            default:
                break;
        }
    }

    handlePopoverClose() {
        this.setState({ popoverFilesTarget: false, selectedFiles: [] });
    }

    removeFile(index) {
        this.setState((prev) => {
            const updated = [...prev.selectedFiles];
            updated.splice(index, 1);
            return { selectedFiles: updated, popoverFilesTarget: updated.length > 0 };
        });
    }

    handleEmojiClick(emojiData) {
        const { typeMessage, onMessageChange } = this.props;
        const newMsg = (typeMessage || "") + emojiData.emoji;
        onMessageChange({ target: { value: newMsg } });
    }

    handleKeyDown(e) {
        if (e.key === "Escape") {
            const { onSelectUser } = this.props;
            onSelectUser && onSelectUser(null);
            e.preventDefault();
        } else if (!e.shiftKey && e.key === "Enter") {
            e.preventDefault();
            this.handleMessageSubmit(e);
        }
    }

    determineMessageType(uploadedFiles) {
        if (!uploadedFiles.length) return "text";
        const mimeType = uploadedFiles[0].file_extension_type?.toLowerCase();
        if (!mimeType) return "text";
        if (mimeType.startsWith("image/")) return "image";
        if (mimeType.startsWith("video/")) return "video";
        if (mimeType === "application/pdf") return "document";
        return "text";
    }

    handleMessageSubmit(e, uploadedPath = []) {
        e.preventDefault();
        if (!this.isSendAllowed()) return;
        const { typeMessage, isReplyMessage, selectedUser, onMessageSubmit, onMessageChange, onReplyChange } = this.props;

        const formData = {
            receiver_contact_id: selectedUser,
            user_message: typeMessage,
            reply_msg_wa_id: isReplyMessage?.whatapp_chat_id ?? "",
            reply_msg_crm_id: isReplyMessage?.id ?? "",
            file_url: uploadedPath?.map((item) => item.file_url).toString() ?? "",
            file_id: uploadedPath?.flatMap((item) => item.file_id).filter((val) => val !== 1).toString() ?? "",
            type: this.determineMessageType(uploadedPath) ?? "text",
        };

        onMessageSubmit && onMessageSubmit(formData);
        this.handlePopoverClose();
        onMessageChange && onMessageChange({ target: { value: "" } });
        onReplyChange && onReplyChange(null);
    }

    render() {
        const {
            typeMessage, isReplyMessage, waMessageChats = [],
            templates = [], selectedUser, campaignInputParams = [],
            scrollToLastMsg, onMessageChange, onReplyChange,
            onUploadFile, onSendTemplate,
        } = this.props;

        const { isMobile, showEmoji, sendTemplate, popoverFilesTarget, selectedFiles } = this.state;
        const isValidUser = waMessageChats[0]?.is_24hour_timeline === true;

        return (
            <div style={{ position: "sticky", bottom: 0, width: "100%", zIndex: 10 }}>
                {/* Reply preview */}
                {isReplyMessage && (
                    <div style={{ padding: "4px 8px", borderLeft: "4px solid var(--primary)", backgroundColor: "#f5f5f5", borderRadius: 4 }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <small>Replying to {isReplyMessage?.user_name ?? "-"}</small>
                            <button
                                className="btn btn-link btn-sm p-0"
                                onClick={() => onReplyChange && onReplyChange(null)}
                            >
                                ✕
                            </button>
                        </div>
                        {isReplyMessage?.media_type === "text" && (
                            <small className="text-muted">{isReplyMessage?.content ?? ""}</small>
                        )}
                        <div className="mt-1">
                            {isReplyMessage?.media_type === "location" && (
                                <span className="badge badge-light p-1">
                                    <i className="fas fa-map-marker-alt text-primary mr-1" />Location
                                </span>
                            )}
                            {isReplyMessage?.media_type === "image" && (
                                <span className="badge badge-light p-1">
                                    <i className="fas fa-image text-primary mr-1" />Photo
                                </span>
                            )}
                            {isReplyMessage?.media_type === "document" && (
                                <span className="badge badge-light p-1">
                                    <i className="fas fa-file-pdf text-primary mr-1" />Document
                                </span>
                            )}
                            {isReplyMessage?.media_type === "video" && (
                                <span className="badge badge-light p-1">
                                    <i className="fas fa-video text-primary mr-1" />Video
                                </span>
                            )}
                            {isReplyMessage?.media_type === "audio" && isReplyMessage?.media_url && (
                                <span className="badge badge-light p-1">
                                    <i className="fas fa-music text-primary mr-1" />Audio
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Main footer bar */}
                <div style={{
                    position: "sticky", bottom: 0, width: "100%",
                    borderTop: "1px solid #e9edef", backgroundColor: "#fff",
                    padding: "6px 8px", display: "flex", alignItems: "center",
                    gap: 4, zIndex: 10,
                }}>
                    {/* Scroll-to-bottom button */}
                    {isReplyMessage === null && (
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={scrollToLastMsg}
                            style={{
                                position: "absolute", bottom: 60, right: 2,
                                borderRadius: "50%", width: 32, height: 32,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                padding: 0,
                            }}
                        >
                            <i className="fas fa-chevron-down" style={{ fontSize: 14 }} />
                        </button>
                    )}

                    {/* Action buttons */}
                    <div className="d-flex align-items-center" style={{ gap: 2, flexShrink: 0 }}>
                        <button
                            className="btn btn-sm"
                            title="Send Template"
                            onClick={() => this.handleSpeedDialChange("template")}
                            style={{
                                width: 32, height: 32, borderRadius: 8,
                                border: "1px solid #1976d2", color: "#1976d2",
                                backgroundColor: "rgba(25,118,210,0.08)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                        >
                            <i className="fas fa-file-alt" />
                        </button>

                        <button
                            className="btn btn-sm"
                            title={isValidUser ? "Send a template first" : "Attach Image/Video"}
                            disabled={isValidUser}
                            onClick={() => this.handleSpeedDialChange("photos")}
                            style={{
                                width: 32, height: 32,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                cursor: isValidUser ? "not-allowed" : "pointer",
                            }}
                        >
                            <i className="fas fa-image" style={{ color: isValidUser ? "#808080" : "#1976d2" }} />
                        </button>

                        <button
                            className="btn btn-sm"
                            title={isValidUser ? "Send a template first" : "Attach Document"}
                            disabled={isValidUser}
                            onClick={() => this.handleSpeedDialChange("documents")}
                            style={{
                                width: 32, height: 32,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                cursor: isValidUser ? "not-allowed" : "pointer",
                            }}
                        >
                            <i className="fas fa-paperclip" style={{ color: isValidUser ? "#808080" : "#1976d2" }} />
                        </button>
                    </div>

                    {/* Text input */}
                    <div style={{ flex: 1, position: "relative" }}>
                        <div
                            className="d-flex align-items-center"
                            style={{
                                background: isValidUser ? "#eceff1" : "#fff",
                                border: "1px solid #d1d7db",
                                borderRadius: 20,
                                padding: "0 6px 0 8px",
                            }}
                        >
                            <span
                                style={{ cursor: isValidUser ? "not-allowed" : "pointer", lineHeight: 1, padding: "0 6px", flexShrink: 0 }}
                                title={isValidUser ? "Send a template first" : "Open Emoji"}
                                onClick={() => !isValidUser && this.setState((prev) => ({ showEmoji: !prev.showEmoji }))}
                            >
                                <i className="far fa-smile" style={{ fontSize: 18, color: showEmoji ? "var(--active-color)" : isValidUser ? "#808080" : "#54656f" }} />
                            </span>
                            <Form.Control
                                as="textarea"
                                disabled={isValidUser}
                                rows={1}
                                value={isValidUser
                                    ? "Send a template message first."
                                    : typeMessage || ""}
                                onChange={onMessageChange}
                                placeholder="Type a message"
                                onKeyDown={(e) => this.handleKeyDown(e)}
                                style={{
                                    border: "none",
                                    boxShadow: "none",
                                    outline: "none",
                                    resize: "none",
                                    background: "transparent",
                                    height: 38,
                                    minHeight: 38,
                                    maxHeight: 110,
                                    padding: "8px 6px",
                                    fontSize: 14,
                                    lineHeight: "20px",
                                    cursor: isValidUser ? "not-allowed" : "text",
                                }}
                            />
                        </div>

                        {/* Emoji picker overlay */}
                        {showEmoji && (
                            <div
                                style={{
                                    position: "absolute", bottom: "110%", left: 0,
                                    zIndex: 1000, borderRadius: 8,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                    maxHeight: 350, overflowY: "auto", width: 350,
                                }}
                            >
                                <EmojiPicker
                                    onEmojiClick={(emojiData) => {
                                        this.handleEmojiClick(emojiData);
                                        this.setState({ showEmoji: false });
                                    }}
                                    disableSearchBar
                                    disableSkinTonePicker
                                    disableAutoFocus={true}
                                    native
                                    height={360}
                                    width="100%"
                                    theme="light"
                                />
                            </div>
                        )}
                    </div>

                    {/* Send button */}
                    <button
                        className="btn"
                        disabled={isValidUser}
                        onClick={(e) => typeMessage ? this.handleMessageSubmit(e) : null}
                        style={{
                            borderRadius: 8, color: "var(--primary)",
                            padding: "0 12px", cursor: isValidUser ? "not-allowed" : "pointer",
                            flexShrink: 0,
                        }}
                    >
                        <i className="fas fa-paper-plane" />
                    </button>
                </div>

                {popoverFilesTarget && (
                    <ConvoMediaFiles
                        open={popoverFilesTarget}
                        handleClose={() => this.handlePopoverClose()}
                        selectedFiles={selectedFiles}
                        removeFile={(i) => this.removeFile(i)}
                        handleMessageSubmit={(e, path) => this.handleMessageSubmit(e, path)}
                        typeMessage={typeMessage}
                        onMessageChange={onMessageChange}
                        onUploadFile={onUploadFile}
                    />
                )}

                {sendTemplate && (
                    <WaTemplates
                        show={sendTemplate}
                        handleClose={() => this.setState({ sendTemplate: false })}
                        templates={templates}
                        selectedUser={selectedUser}
                        campaignInputParams={campaignInputParams}
                        onSendTemplate={onSendTemplate}
                    />
                )}
            </div>
        );
    }
}

export default ConvoFooter;
